import React, { MouseEvent, TouchEvent } from "react";
import { Grid } from "@mui/material";
import { DroppsProduct, ScentDetails, productSelect } from "@/store";
import { SvgSrcIcon } from "@/components/SvgSrcIcon";
import { storefrontApi } from "@/services/storefront";
import { useHasProductRouter } from "./ProductRouter";
import { usePdpActions } from "./PdpContext";

type ScentButtonProps = {
  scent: ScentDetails;
  focused: boolean;
  hoverAction?: boolean;
  showScentName?: boolean;
  highlightUnfocused?: boolean;
  showRing?: boolean;
  enableRedirectWhenOOS?: boolean;
};

export const ScentButton = ({
  scent,
  focused,
  hoverAction = true,
  showScentName = false,
  highlightUnfocused = true,
  showRing = true,
  enableRedirectWhenOOS = false,
}: ScentButtonProps) => {
  const actions = usePdpActions();
  const isRoutable = useHasProductRouter();
  const url = productSelect.url(scent);
  const product = storefrontApi.useHandle(scent.handle);

  const isOutOfStock =
    product.data === undefined
      ? false
      : product.data.variants.every((v) => !v.available);

  const shouldRedirect = isOutOfStock && enableRedirectWhenOOS;

  const handleScentUnfocus = () => actions.unfocusProduct();
  const handleScentFocus =
    product.data === undefined
      ? undefined
      : () => actions.focusProduct(product.data);
  const handleScentSelect =
    (product: DroppsProduct) => (e: MouseEvent<HTMLElement>) => {
      if (shouldRedirect) {
        if (!isRoutable) {
          window.location.href = url;
        }
        return;
      }
      e.preventDefault();
      console.log("selecting product", product);
      actions.selectProduct(product);
    };
  const handleTouchEnd =
    (product: DroppsProduct) => (e: TouchEvent<HTMLElement>) => {
      if (shouldRedirect) {
        if (!isRoutable) {
          window.location.href = url;
        }
        return;
      }
      e.preventDefault();
      console.log("selecting product via touch", product);
      actions.focusProduct(product);
      actions.selectProduct(product);
    };

  const buttonProps = hoverAction
    ? {
        onClick:
          product.data === undefined
            ? undefined
            : handleScentSelect(product.data),
        onTouchEnd:
          product.data === undefined ? undefined : handleTouchEnd(product.data),
        onFocus: handleScentFocus,
        onBlur: handleScentUnfocus,
      }
    : {
        onClick:
          product.data === undefined
            ? undefined
            : handleScentSelect(product.data),
        onTouchEnd:
          product.data === undefined ? undefined : handleTouchEnd(product.data),
      };

  const child = showScentName ? (
    <div className="font-myriad-pro-condensed max-w-fit rounded-full px-4 py-2 text-xl font-semibold uppercase text-black">
      {scent.name}
    </div>
  ) : (
    <SvgSrcIcon src={scent.icon} height={40} width={40} title={scent.name} />
  );

  return (
    <Grid
      item
      onMouseEnter={hoverAction ? handleScentFocus : undefined}
      onMouseLeave={hoverAction ? handleScentUnfocus : undefined}
    >
      <div
        data-id="scents-button"
        className={`flex rounded-full transition-all ${
          showRing
            ? focused
              ? "ring-2 ring-primary"
              : highlightUnfocused
              ? "ring-1 ring-primary"
              : "ring-2 ring-transparent"
            : ""
        }`}
        style={{ backgroundColor: scent.color }}
      >
        {isRoutable ? (
          <a
            aria-label={scent.name}
            href={url}
            {...buttonProps}
            onClick={shouldRedirect ? undefined : buttonProps.onClick}
            onTouchEnd={shouldRedirect ? undefined : buttonProps.onTouchEnd}
          >
            {child}
          </a>
        ) : (
          <button
            className="border-none bg-transparent p-0"
            {...buttonProps}
            title={"Set scent to " + scent.name}
          >
            {child}
          </button>
        )}
      </div>
    </Grid>
  );
};
